// @refresh reset
import { createContext, useContext, useState, type ReactNode } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { apiRequest, queryClient, getQueryFn } from "@/lib/queryClient";

type ImpersonationInfo = {
  tenantId: string;
  tenantName?: string;
};

type AuthUser = {
  id: string;
  email: string;
  displayName: string;
  role: "super_admin" | "tenant_admin" | "supervisor" | "agent";
  tenantId: string | null;
  tenantName: string | null;
  impersonating: ImpersonationInfo | null;
  canSeeBilling: boolean;
};

type TwoFactorPending = {
  email: string;
};

type AuthContextType = {
  user: AuthUser | null;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  startImpersonation: (tenantId: string) => Promise<void>;
  exitImpersonation: () => Promise<void>;
  isImpersonating: boolean;
  impersonatingTenantName: string | null;
  twoFactorPending: TwoFactorPending | null;
  clearTwoFactor: () => void;
};

const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [twoFactorPending, setTwoFactorPending] = useState<TwoFactorPending | null>(null);

  const { data: user, isLoading } = useQuery<AuthUser | null>({
    queryKey: ["/api/auth/me"],
    queryFn: getQueryFn({ on401: "returnNull" }),
    staleTime: Infinity,
    retry: false,
  });

  const loginMutation = useMutation({
    mutationFn: async ({ email, password }: { email: string; password: string }) => {
      const res = await apiRequest("POST", "/api/auth/login", { email, password });
      return res.json();
    },
    onSuccess: (data: any) => {
      if (data.requiresTwoFactor) {
        setTwoFactorPending({ email: data.email });
        // Clear the authenticated user so the heartbeat stops running.
        // Without this the heartbeat fires against the now-pending-2FA session,
        // gets 401, and calls logout() — wiping the 2FA state before the user
        // can enter the code.
        queryClient.setQueryData(["/api/auth/me"], null);
      } else {
        setTwoFactorPending(null);
        if (data.id) {
          queryClient.setQueryData(["/api/auth/me"], {
            id: data.id,
            email: data.email,
            displayName: data.displayName,
            role: data.role,
            tenantId: data.tenantId,
            tenantName: null,
            impersonating: null,
          });
        }
        queryClient.invalidateQueries({ queryKey: ["/api/auth/me"] });
        // Safety-net: after re-login the Plivo onLogin handler may fire before the
        // session cookie is stable (stale window.Plivo connection) and exhaust its
        // retries without ever refreshing data queries.  Force-invalidate all data
        // queries after 3 s so the dashboard always shows fresh state on re-login.
        setTimeout(() => {
          queryClient.invalidateQueries();
        }, 3000);
      }
    },
  });

  const logoutMutation = useMutation({
    mutationFn: async () => {
      await apiRequest("POST", "/api/auth/logout");
    },
    onSuccess: () => {
      queryClient.setQueryData(["/api/auth/me"], null);
      queryClient.clear();
      setTwoFactorPending(null);
    },
  });

  const login = async (email: string, password: string) => {
    await loginMutation.mutateAsync({ email, password });
  };

  const logout = async () => {
    try {
      await logoutMutation.mutateAsync();
    } catch {
      // Server session may already be expired (e.g. inactivity auto-logout while
      // the laptop was closed). The API call fails with 401, so onSuccess never
      // fires. Clear local auth state directly so the UI shows the login page.
      queryClient.setQueryData(["/api/auth/me"], null);
      queryClient.clear();
      setTwoFactorPending(null);
    }
  };

  const clearTwoFactor = () => {
    setTwoFactorPending(null);
  };

  const startImpersonation = async (tenantId: string) => {
    await apiRequest("POST", "/api/auth/impersonate", { tenantId });
    queryClient.removeQueries({ predicate: (q) => q.queryKey[0] !== "/api/auth/me" });
    await queryClient.refetchQueries({ queryKey: ["/api/auth/me"] });
  };

  const exitImpersonation = async () => {
    await apiRequest("POST", "/api/auth/exit-impersonation");
    queryClient.removeQueries({ predicate: (q) => q.queryKey[0] !== "/api/auth/me" });
    await queryClient.refetchQueries({ queryKey: ["/api/auth/me"] });
  };

  const isImpersonating = !!(user?.impersonating);
  const impersonatingTenantName = user?.impersonating?.tenantName || null;

  return (
    <AuthContext.Provider value={{
      user: user ?? null,
      isLoading,
      login,
      logout,
      startImpersonation,
      exitImpersonation,
      isImpersonating,
      impersonatingTenantName,
      twoFactorPending,
      clearTwoFactor,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within AuthProvider");
  return context;
}
